import { RouterFramework, useRouter } from '@egw/router'
import cx from 'classnames'
import NextLink from 'next/link'
import { useMemo, type FC } from 'react'
import { Link as ReactLink } from 'react-router'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'

enum LinkType {
  External = 'external',
  NextLinkType = 'next',
  ReactLinkType = 'react',
}

export enum LinkVariant {
  default = 'default',
  primary = 'primary',
  secondary = 'secondary',
}
export interface LinkProps extends DefaultProps {
  /**
   * The URL to link to.
   */
  href: string
  title?: string
  onClick?: () => void
  variant?: LinkVariant
}

export const Link: FC<LinkProps> = ({
  // Common default properties for each component
  className,
  id,
  testId,
  ariaLabel,
  children,
  href,
  title,
  onClick,
  variant = LinkVariant.default,
}) => {
  const { framework } = useRouter()

  const linkType = useMemo((): LinkType => {
    const externalLinkCheck = /^(?:http|https):\/\//i

    if (externalLinkCheck.test(href)) return LinkType.External
    else if (framework === RouterFramework.Next) return LinkType.NextLinkType
    return LinkType.ReactLinkType
  }, [href, framework])

  // Component base class names. Can be overriden by className property.
  const baseClass = cx('text-action font-semibold text-base', {
    'external-link': linkType === LinkType.External,
    'next-link': linkType === LinkType.NextLinkType,
    'react-link': linkType === LinkType.ReactLinkType,
  })

  const variantClass = cx({
    'font-semibold border rounded-[0.3rem] whitespace-nowrap focus-visible:outline-transparent text-center flex items-center justify-center px-[24px] py-[12px] text-base text-btnPrimaryText disabled:text-btnPrimaryTextDisabled active:text-btnPrimaryTextActive hover:text-btnPrimaryTextHover bg-btnPrimaryBg disabled:bg-btnPrimaryBgDisabled active:bg-btnPrimaryBgActive hover:bg-btnPrimaryBgHover':
      variant === LinkVariant.primary,
    'font-semibold border rounded-[0.3rem] whitespace-nowrap focus-visible:outline-transparent text-center flex items-center justify-center px-[24px] py-[12px] text-base bg-btnSecondaryBg disabled:bg-btnSecondaryBgDisabled active:bg-btnSecondaryBgActive hover:bg-btnSecondaryBgHover text-btnSecondaryText disabled:text-btnSecondaryTextDisabled active:text-btnSecondaryTextActive hover:text-btnSecondaryTextHover border-btnSecondaryBorder active:border-btnSecondaryBorderActive hover:border-btnSecondaryBorderHover':
      variant === LinkVariant.secondary,
  })

  // Final class names
  const finalClasses = twMerge(baseClass, variantClass, className)

  if (linkType === LinkType.External) {
    return (
      <a
        className={finalClasses}
        {...(testId ? { 'data-testid': testId } : {})}
        {...(id ? { id } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={title}
        onClick={onClick}
      >
        {children}
      </a>
    )
  }

  if (linkType === LinkType.NextLinkType) {
    return (
      <NextLink
        className={finalClasses}
        {...(testId ? { 'data-testid': testId } : {})}
        {...(id ? { id } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        href={href}
        title={title}
        onClick={onClick}
      >
        {children}
      </NextLink>
    )
  }

  // Default to ReactLink
  return (
    <ReactLink
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      to={href}
      title={title}
      onClick={onClick}
    >
      {children}
    </ReactLink>
  )
}
